import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Loader2 } from 'lucide-react';

type AdminRole = 'superadmin' | 'admin' | 'moderator' | 'viewer';

interface ProtectedAdminRouteProps {
  children: ReactNode;
  minRole?: AdminRole;
}

export function ProtectedAdminRoute({ children, minRole = 'viewer' }: ProtectedAdminRouteProps) {
  const { user, adminUser, isLoading, hasRole } = useAdminAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-admin-bg flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-admin-accent" />
          <p className="text-sm text-admin-muted animate-pulse">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login/administration" state={{ from: location }} replace />;
  }

  if (!adminUser) {
    return (
      <div className="min-h-screen bg-admin-bg flex items-center justify-center p-4">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/10 flex items-center justify-center">
            <span className="text-3xl">🚫</span>
          </div>
          <h1 className="text-xl font-semibold text-admin-text mb-2">Access Denied</h1>
          <p className="text-admin-muted mb-6">
            Your account is not authorized to access the admin panel. 
            If you believe this is an error, contact a superadmin.
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className="px-4 py-2 rounded-lg bg-admin-accent text-white text-sm font-medium hover:bg-admin-accent/90 transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  if (!hasRole(minRole)) {
    return (
      <div className="min-h-screen bg-admin-bg flex items-center justify-center p-4">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-500/10 flex items-center justify-center">
            <span className="text-3xl">⚠️</span>
          </div>
          <h1 className="text-xl font-semibold text-admin-text mb-2">Insufficient Permissions</h1>
          <p className="text-admin-muted mb-6">
            Your role ({adminUser.role}) doesn't have permission to access this section.
            Required role: {minRole} or higher.
          </p>
          <button
            onClick={() => window.location.href = '/admin'}
            className="px-4 py-2 rounded-lg bg-admin-accent text-white text-sm font-medium hover:bg-admin-accent/90 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
